import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminFormPage, saveErrorToast } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { LanguageFieldForm } from "../LanguageFieldForm/LanguageFieldForm";
import { useLanguageFieldMutations } from "../api";
import { emptyLanguageFieldForm } from "../types";
import type { LanguageFieldFormValues } from "../types";

const FORM_ID = "language-field-form";

export const New = (): JSX.Element => {
  const navigate = useNavigate();
  const { create } = useLanguageFieldMutations();
  const [submitting, setSubmitting] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);
  const nextAction = useRef<"edit" | "new">("edit");

  const handleSubmit = async (values: LanguageFieldFormValues) => {
    setSubmitting(true);
    const addAnother = nextAction.current === "new";
    try {
      const result: any = await create(values);
      const newId = result?.data?.id;
      if (addAnother) {
        setFormKey((k) => k + 1);
        setToast({ variant: "success", text: `'${values.name}' saved` });
        setSubmitting(false);
      } else {
        navigate(newId ? `../${newId}/edit` : "..", {
          relative: "path",
          state: {
            toast: { variant: "success", text: `'${values.name}' saved` },
          },
        });
      }
    } catch (err) {
      console.error("Failed to create language field", err);
      setToast(saveErrorToast(err, "language field"));
      setSubmitting(false);
    } finally {
      nextAction.current = "edit";
    }
  };

  const triggerAddAnother = () => {
    nextAction.current = "new";
    (
      document.getElementById(FORM_ID) as HTMLFormElement | null
    )?.requestSubmit();
  };

  return (
    <>
      <AdminFormPage
        title="Add a Language Field"
        formId={FORM_ID}
        onCancel={() => navigate("..")}
        onSaveAndAddAnother={triggerAddAnother}
        isSubmitting={submitting}
      >
        <LanguageFieldForm
          key={formKey}
          id={FORM_ID}
          initialValues={emptyLanguageFieldForm}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};
